import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getReportsData, saveReportsForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { REPORTS } from '../../../formQuestionsEnum';
import ChecklistForm from './ChecklistForm';
import EMARDAtaForm from './EMARDataForm';
import TimeclockForm from './TimeclockForm';

const ReportsView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const reportsPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.reportsFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getReportsData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(REPORTS).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const result = trimData(allData);

    await saveReportsForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Reports data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Reports data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <ChecklistForm permissions={reportsPermissions} />
      <EMARDAtaForm permissions={reportsPermissions} />
      <TimeclockForm permissions={reportsPermissions} />
    </>
  );
});

export default ReportsView;

ReportsView.propTypes = {
  setFormData: PropTypes.func,
};
